import React, { useEffect, useContext, useState, useReducer } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import "react-datepicker/dist/react-datepicker.css";

import {
  batchEmailBulletins,
  batchEmailBulletinsIndividual,
  getBulletinIdGroups,
  postBulkUpdateGroups,
  postBulletinShare,
  GroupResourceWithBulletinMembership,
} from "@rubi/api-generated";

import { AlertViewContext } from "@/components/alerts/context";
import { AuthStateContext } from "@/contexts/AuthContext";

import IndividualEmails from "./IndividualEmails";
import NotifyEmailComment from "./NotifyEmailComment";
import NotifyEmailTable from "./NotifyEmailTable";
import {
  BulletinAccessAction,
  BulletinAccessState,
  NotifyAction,
  NotifyState,
  PortalAccessCheckboxStatus,
  potentialGroupsState,
  getGroupStatusByBulletinId,
} from "./types";

export type {
  BulletinAccessState,
  BulletinAccessAction,
  potentialGroupsState,
  NotifyState,
  NotifyAction,
};
export { PortalAccessCheckboxStatus, getGroupStatusByBulletinId };

// Define steps
enum Steps {
  SELECT_RECIPIENTS = 0,
  ADD_COMMENT = 1,
}

// this is all the possible groups that this user can access.
// from business logic it contains all the non-self groups and the public group
// this user belongs to. However, we need to put the groups that user belongs to
// in the sharedGroups state and the groups that user does not belong to
// in the otherGroups state.
const potentialGroupsReducer = (state: potentialGroupsState, action: any) => {
  switch (action.type) {
    case "setUserBelongedGroups":
      return { ...state, userBelongedGroups: action.payload };
    case "setOtherGroups":
      return { ...state, otherGroups: action.payload };
    case "setPublicGroup":
      return { ...state, publicGroup: action.payload };
    default:
      return state;
  }
};

const bulletinAccessReducer = (
  state: BulletinAccessState,
  action: BulletinAccessAction,
): BulletinAccessState => {
  switch (action.type) {
    case "SET_BULLETINS_AND_ITS_GROUPS": {
      const { bulletinId, groups } = action.payload;
      const bulletinExists = state.some((b) => b.bulletinId === bulletinId);
      if (bulletinExists) {
        return state.map((b) =>
          b.bulletinId === bulletinId ? { ...b, sharedGroups: groups } : b,
        );
      } else {
        return [...state, { bulletinId, sharedGroups: groups }];
      }
    }

    case "ADD_GROUP_TO_ALL_BULLETINS": {
      const { group } = action.payload;
      return state.map((bulletin) => {
        const existingGroupIndex = bulletin.sharedGroups.findIndex(
          (g) => g.id === group.id,
        );

        // If group doesn't exist, add it directly
        if (existingGroupIndex === -1) {
          return {
            ...bulletin,
            sharedGroups: [...bulletin.sharedGroups, group],
          };
        }

        const existingGroup = bulletin.sharedGroups[existingGroupIndex];

        // If time_expires is different, update the group information
        if (existingGroup.time_expires !== group.time_expires) {
          const updatedGroups = [...bulletin.sharedGroups];
          updatedGroups[existingGroupIndex] = group;
          return {
            ...bulletin,
            sharedGroups: updatedGroups,
          };
        }

        // If group already exists and time_expires is the same, don't make any changes
        return bulletin;
      });
    }

    case "REMOVE_GROUP_FROM_BULLETINS": {
      const { groupId } = action.payload;
      return state.map((bulletin) => ({
        ...bulletin,
        sharedGroups: bulletin.sharedGroups.filter((g) => g.id !== groupId),
      }));
    }

    default:
      return state;
  }
};

const notifyReducer = (
  state: NotifyState,
  action: NotifyAction,
): NotifyState => {
  switch (action.type) {
    case "TOGGLE_GROUP_NOTIFY":
      return {
        ...state,
        notifiedGroups: state.notifiedGroups.includes(action.group)
          ? state.notifiedGroups.filter((group) => group.id !== action.group.id)
          : [...state.notifiedGroups, action.group],
      };
    case "SET_INCLUDE_MYSELF":
      return {
        ...state,
        includeMyself: !state.includeMyself,
      };
    case "SET_INDIVIDUAL_EMAILS":
      return {
        ...state,
        individualEmails: action.emails,
      };
    case "ADD_INDIVIDUAL_EMAIL":
      return {
        ...state,
        individualEmails: [...state.individualEmails, action.email],
      };
    case "REMOVE_INDIVIDUAL_EMAIL":
      return {
        ...state,
        individualEmails: state.individualEmails.filter(
          (email) => email !== action.email,
        ),
      };
    case "RESET":
      return {
        notifiedGroups: [],
        includeMyself: false,
        individualEmails: [],
      };
    default:
      return state;
  }
};

export function userCanToggleBulletinAccess() {}

export function userChangedBulletinAccess(
  initialBulletinAccessState: BulletinAccessState,
  bulletinAccessState: BulletinAccessState,
) {
  // Get the set of all bulletinIds from both states (using Set for automatic deduplication)
  const initialBulletinIds = new Set(
    initialBulletinAccessState.map((b) => b.bulletinId),
  );
  const currentBulletinIds = new Set(
    bulletinAccessState.map((b) => b.bulletinId),
  );

  // Check if the bulletinId sets are different (regardless of order)
  if (initialBulletinIds.size !== currentBulletinIds.size) {
    return true;
  }

  // Check if any bulletinId exists in one set but not in the other
  for (const id of initialBulletinIds) {
    if (!currentBulletinIds.has(id)) {
      return true;
    }
  }

  // Check shared groups within each bulletin
  for (const bulletinId of initialBulletinIds) {
    const initialBulletin = initialBulletinAccessState.find(
      (b) => b.bulletinId === bulletinId,
    );
    const currentBulletin = bulletinAccessState.find(
      (b) => b.bulletinId === bulletinId,
    );

    if (!initialBulletin || !currentBulletin) {
      continue; // This shouldn't execute theoretically, as we've already checked the id sets
    }

    // Get all groupId sets for the current bulletin in both states
    const initialGroupIds = new Set(
      initialBulletin.sharedGroups.map((g) => g.id),
    );
    const currentGroupIds = new Set(
      currentBulletin.sharedGroups.map((g) => g.id),
    );

    // Check if groupId sets are different (regardless of order)
    if (initialGroupIds.size !== currentGroupIds.size) {
      return true;
    }

    // Check if any groupId exists in one set but not in the other
    for (const id of initialGroupIds) {
      if (!currentGroupIds.has(id)) {
        return true;
      }
    }

    // For groups that exist in both states, check if time_expires and time_revoked have changed
    // Note: We don't check user_id_access_granter and user_id_access_revoker
    for (const groupId of initialGroupIds) {
      const initialGroup = initialBulletin.sharedGroups.find(
        (g) => g.id === groupId,
      );
      const currentGroup = currentBulletin.sharedGroups.find(
        (g) => g.id === groupId,
      );

      if (!initialGroup || !currentGroup) {
        continue; // This shouldn't execute theoretically
      }

      // Check if time_expires has changed
      if (
        initialGroup.time_expires?.toString() !==
        currentGroup.time_expires?.toString()
      ) {
        return true;
      }

      // Check if time_revoked has changed
      if (
        initialGroup.time_revoked?.toString() !==
        currentGroup.time_revoked?.toString()
      ) {
        return true;
      }
    }
  }

  // No differences found
  return false;
}

const NotifyEmails = ({
  bulletinIds,
  closeShareDialog,
}: {
  bulletinIds: number[];
  closeShareDialog: () => void;
}) => {
  const { policeServices } = useContext(AlertViewContext);

  // Add activity step state
  const [activeStep, setActiveStep] = useState<Steps>(Steps.SELECT_RECIPIENTS);
  const [bulletinAccessState, setBulletinAccessStateDispatch] = useReducer(
    bulletinAccessReducer,
    [],
  );
  const [initialBulletinAccessState, setInitialBulletinAccessState] =
    useReducer(bulletinAccessReducer, []);
  const [potentialGroups, setPotentialGroupsDispatch] = useReducer(
    potentialGroupsReducer,
    {
      userBelongedGroups: [],
      otherGroups: [],
      publicGroup: null,
    },
  );
  const [notifyState, setNotifyStateDispatch] = useReducer(notifyReducer, {
    notifiedGroups: [],
    includeMyself: false,
    individualEmails: [],
  });

  const [note, setNote] = useState("");
  const { t } = useTranslation("bulletin/share");
  const { currentUserGroups } = useContext(AuthStateContext);

  useEffect(() => {
    const fetchData = async () => {
      const allGroups = policeServices;
      const groupsUserBelonged = currentUserGroups.filter(
        (group) => !group.is_self_group,
      );
      const groupsUserNotBelonged = allGroups.filter((group) => {
        return !groupsUserBelonged.map((group) => group.id).includes(group.id);
      });
      const publicGroup = groupsUserBelonged.find((group) => group.is_public);
      const groupsUserBelongedWithoutPublic = groupsUserBelonged.filter(
        (group) => !group.is_public,
      );

      setPotentialGroupsDispatch({
        type: "setUserBelongedGroups",
        payload: groupsUserBelongedWithoutPublic,
      });
      setPotentialGroupsDispatch({
        type: "setOtherGroups",
        payload: groupsUserNotBelonged,
      });
      setPotentialGroupsDispatch({
        type: "setPublicGroup",
        payload: publicGroup,
      });
      Promise.all(
        bulletinIds.map(async (bulletinId) => {
          // for each bulletin, get the groups that are shared to it
          const response = await getBulletinIdGroups({
            path: { bulletin_id: bulletinId },
          });
          const sharedGroups = (response.data?.items ?? []).filter(
            (item) => !item.is_self_group,
          );
          setBulletinAccessStateDispatch({
            type: "SET_BULLETINS_AND_ITS_GROUPS",
            payload: { bulletinId, groups: sharedGroups },
          });
          setInitialBulletinAccessState({
            type: "SET_BULLETINS_AND_ITS_GROUPS",
            payload: { bulletinId, groups: sharedGroups },
          });
        }),
      );
    };
    fetchData();
  }, [bulletinIds]);

  // Handle back button click
  const handleBack = () => {
    setActiveStep(Steps.SELECT_RECIPIENTS);
  };

  const changePortalAccess = (group_id: number, time_expires: Date | null) => {
    const allGroups = [
      ...potentialGroups.userBelongedGroups,
      ...potentialGroups.otherGroups,
      potentialGroups.publicGroup,
    ];
    const groupToToggle = allGroups.find((g) => g.id === group_id);

    if (!groupToToggle) return;

    // Create an object conforming to GroupResourceWithBulletinMembership.
    // The picker gives a Date; the generated type stores time_expires as an
    // ISO string (matching the backend), so convert here.
    const groupWithMembership: GroupResourceWithBulletinMembership = {
      ...groupToToggle,
      time_expires: time_expires ? time_expires.toISOString() : null,
      time_revoked: null,
      user_id_access_granter: null,
      user_id_access_revoker: null,
      source_user_id: null,
      source_group_id: null,
      source_org_id: null,
      is_content_moderator: false,
      is_owner: false,
    };

    // Get the current status of the group
    const currentStatus = getGroupStatusByBulletinId(
      bulletinAccessState,
      group_id,
    );

    if (time_expires !== null) {
      // If time_expires is not null, it's definitely an add or modify operation
      // Regardless of whether the group exists, remove it first then add it to ensure update
      setBulletinAccessStateDispatch({
        type: "REMOVE_GROUP_FROM_BULLETINS",
        payload: { groupId: group_id },
      });
      setBulletinAccessStateDispatch({
        type: "ADD_GROUP_TO_ALL_BULLETINS",
        payload: { group: groupWithMembership },
      });
    } else {
      // If time_expires is null, we need to determine whether to add or remove/select all

      // If status is INDETERMINATE (group exists in some but not all bulletins), toggle will select this group for all
      if (currentStatus === PortalAccessCheckboxStatus.INDETERMINATE) {
        // Add to all bulletins (select all)
        setBulletinAccessStateDispatch({
          type: "ADD_GROUP_TO_ALL_BULLETINS",
          payload: { group: groupWithMembership },
        });
      } else {
        // For CHECKED and UNCHECKED states, maintain the original logic
        const groupExists = bulletinAccessState.some((bulletin) =>
          bulletin.sharedGroups.some((group) => group.id === group_id),
        );

        if (groupExists) {
          // If group already exists, remove it
          setBulletinAccessStateDispatch({
            type: "REMOVE_GROUP_FROM_BULLETINS",
            payload: { groupId: group_id },
          });
        } else {
          // If group doesn't exist, add it (with expire as null)
          setBulletinAccessStateDispatch({
            type: "ADD_GROUP_TO_ALL_BULLETINS",
            payload: { group: groupWithMembership },
          });
        }
      }
    }
  };

  const postPortalAccess = async () => {
    // Find newly added groups and groups with changed expiration dates.
    // expire is an ISO string (from GroupResourceWithBulletinMembership) or null.
    const addedGroups: { id: number; expire: string | null }[] = [];
    // Find deleted groups
    const removedGroups: number[] = [];

    // Iterate through each bulletin's current shared groups
    bulletinAccessState.forEach((bulletin) => {
      const initialBulletin = initialBulletinAccessState.find(
        (b) => b.bulletinId === bulletin.bulletinId,
      );

      if (!initialBulletin) return;

      // Process each group in the current bulletin
      bulletin.sharedGroups.forEach((currentGroup) => {
        // Check if group exists in initial state
        const initialGroup = initialBulletin.sharedGroups.find(
          (g) => g.id === currentGroup.id,
        );

        if (!initialGroup) {
          // Newly added group
          // Check if already in addedGroups
          const existingGroup = addedGroups.find(
            (g) => g.id === currentGroup.id,
          );
          if (!existingGroup) {
            addedGroups.push({
              id: currentGroup.id,
              expire: currentGroup.time_expires,
            });
          }
        } else {
          if (initialGroup.time_expires !== currentGroup.time_expires) {
            const existingGroup = addedGroups.find(
              (g) => g.id === currentGroup.id,
            );
            if (!existingGroup) {
              addedGroups.push({
                id: currentGroup.id,
                expire: currentGroup.time_expires,
              });
            }
          }
        }
      });
    });

    // Find completely removed groups (exist in initialBulletinAccessState but not in bulletinAccessState)
    initialBulletinAccessState.forEach((initialBulletin) => {
      const currentBulletin = bulletinAccessState.find(
        (b) => b.bulletinId === initialBulletin.bulletinId,
      );

      if (!currentBulletin) return;

      // Check which groups from the initial bulletin were deleted
      initialBulletin.sharedGroups.forEach((group) => {
        // If group is not in current state, it was deleted
        const groupInCurrent = currentBulletin.sharedGroups.some(
          (g) => g.id === group.id,
        );

        if (!groupInCurrent && !removedGroups.includes(group.id)) {
          removedGroups.push(group.id);
        }
      });
    });

    // If no changes, return directly
    if (addedGroups.length === 0 && removedGroups.length === 0) {
      return { updated: false };
    }

    // Get all relevant bulletinIds
    const bulletinIds = bulletinAccessState.map(
      (bulletin) => bulletin.bulletinId,
    );

    try {
      // Process each bulletin and group combination individually
      const addPromises: Promise<any>[] = [];
      const removePromises: Promise<any>[] = [];

      // Handle additions and updates
      for (const bulletinId of bulletinIds) {
        for (const group of addedGroups) {
          addPromises.push(
            postBulletinShare({
              body: {
                bulletin_id: bulletinId,
                destination_group_id: group.id,
                time_expires: group.expire,
              },
            }),
          );
        }
      }

      // Handle removals
      for (const bulletinId of bulletinIds) {
        for (const groupId of removedGroups) {
          removePromises.push(
            postBulkUpdateGroups({
              body: {
                add_groups: [],
                remove_groups: [groupId],
                bulletinIds: [bulletinId],
              },
            }),
          );
        }
      }

      // Wait for all operations to complete
      const [addResults, removeResults] = await Promise.all([
        Promise.all(addPromises),
        Promise.all(removePromises),
      ]);

      return {
        updated: true,
        addedGroups,
        removedGroups,
        addResponse: addResults,
        removeResponse: removeResults,
      };
    } catch (error) {
      console.error("Failed to update portal access:", error);
      throw error;
    }
  };

  const postNotifyEmails = async () => {
    let isNotificationSent = false;
    let failedToSend: any = {};

    try {
      // Check if there are any notifications to send
      if (
        notifyState.notifiedGroups.length > 0 ||
        notifyState.includeMyself ||
        notifyState.individualEmails.length > 0
      ) {
        // Send to groups
        if (
          notifyState.notifiedGroups.length > 0 ||
          notifyState.includeMyself
        ) {
          const groupRes = await batchEmailBulletins({
            body: {
              bulletin_ids: bulletinIds,
              group_ids: notifyState.notifiedGroups.map((group) => group.id),
              note,
              include_myself: notifyState.includeMyself,
            },
          });
          const groupData = groupRes.data as
            | { failed_to_send?: Record<string, unknown> }
            | undefined;

          if (Object.keys(groupData?.failed_to_send || {}).length > 0) {
            failedToSend = {
              ...failedToSend,
              ...groupData?.failed_to_send,
            };
          } else {
            isNotificationSent = true;
          }
        }

        // Send to individual emails
        if (notifyState.individualEmails.length > 0) {
          const individualRes = await batchEmailBulletinsIndividual({
            body: {
              bulletin_ids: bulletinIds,
              individual_emails: notifyState.individualEmails,
              note,
            },
          });
          const individualData = individualRes.data as
            | { failed_to_send?: Record<string, unknown> }
            | undefined;

          if (Object.keys(individualData?.failed_to_send || {}).length > 0) {
            failedToSend = {
              ...failedToSend,
              ...individualData?.failed_to_send,
            };
          } else {
            isNotificationSent = true;
          }
        }

        // Display sending results
        if (Object.keys(failedToSend).length > 0) {
          let errorMsg = "Problem sending notifications\n";
          Object.entries(failedToSend).forEach(([key, value]) => {
            errorMsg += `bulletin ${key}: ${value}\n`;
          });
          toast.error(errorMsg);
          return { success: false, errors: failedToSend };
        } else if (isNotificationSent) {
          toast.success("Notifications sent successfully");
          return { success: true };
        }
      }

      return { success: false, message: "No notifications to send" };
    } catch (error) {
      console.error("Error sending notifications:", error);
      toast.error("Failed to send notifications");
      return { success: false, error };
    }
  };

  const NextButton = () => {
    // Use userChangedBulletinAccess to check if bulletin access state has changed
    const isAccessChanged = userChangedBulletinAccess(
      initialBulletinAccessState,
      bulletinAccessState,
    );

    // Check if there are any notifications
    const isNotifyingAnyone =
      notifyState.notifiedGroups.length > 0 ||
      notifyState.includeMyself ||
      notifyState.individualEmails.length > 0;

    // Button is disabled if there are no changes
    const isDisabled = !isNotifyingAnyone && !isAccessChanged;

    // Get button text
    const getButtonText = () => {
      if (activeStep === Steps.SELECT_RECIPIENTS) {
        if (isNotifyingAnyone) {
          return t("next");
        } else {
          return t("update_access");
        }
      } else if (activeStep === Steps.ADD_COMMENT) {
        return isAccessChanged ? t("notify_and_update_access") : t("notify");
      }
      return "";
    };

    // Comment step button component
    if (activeStep === Steps.ADD_COMMENT) {
      return (
        <Button
          variant="contained"
          color="primary"
          disabled={isDisabled}
          onClick={async () => {
            try {
              // Send notifications
              await postNotifyEmails();
              closeShareDialog();
            } catch (error) {
              console.error("Error sending notifications:", error);
              toast.error("An error occurred. Please try again.");
            }
          }}
        >
          {getButtonText()}
        </Button>
      );
    }

    // Recipient selection step button component
    return (
      <Button
        variant="contained"
        color="primary"
        disabled={isDisabled}
        onClick={async () => {
          try {
            // If access permissions have changed, submit these changes first
            if (isAccessChanged) {
              const result = await postPortalAccess();
              if (result && result.updated) {
                // Update initial state to match current state
                bulletinAccessState.forEach((bulletin) => {
                  setInitialBulletinAccessState({
                    type: "SET_BULLETINS_AND_ITS_GROUPS",
                    payload: {
                      bulletinId: bulletin.bulletinId,
                      groups: [...bulletin.sharedGroups],
                    },
                  });
                });
                toast.success("Access updated successfully");
              }
            }

            // If there are notification recipients, go to comment step
            if (isNotifyingAnyone) {
              setActiveStep(Steps.ADD_COMMENT);
            } else {
              // If only access permissions changed but no notification recipients, close dialog directly
              closeShareDialog();
            }
          } catch (error) {
            console.error("Error updating access:", error);
            toast.error(
              "An error occurred while updating access. Please try again.",
            );
          }
        }}
      >
        {getButtonText()}
      </Button>
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case Steps.SELECT_RECIPIENTS:
        return (
          <>
            <NotifyEmailTable
              bulletinAccessState={bulletinAccessState}
              notifyState={notifyState}
              potentialGroups={potentialGroups}
              setBulletinAccessStateDispatch={setBulletinAccessStateDispatch}
              setNotifyStateDispatch={setNotifyStateDispatch}
              changePortalAccess={changePortalAccess}
            />

            {/* Individual email addresses */}
            <IndividualEmails
              individualEmails={notifyState.individualEmails}
              setIndividualEmails={(emails) =>
                setNotifyStateDispatch({
                  type: "SET_INDIVIDUAL_EMAILS",
                  emails,
                })
              }
            />
          </>
        );
      case Steps.ADD_COMMENT:
        return (
          <div>
            {/* Display selected notification object summary */}
            <div className="mb-3">
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t("recipients_summary")}
              </Typography>
              <Box pl={2}>
                <div className="">
                  {notifyState.notifiedGroups.length > 0 &&
                    notifyState.notifiedGroups.map((group) => (
                      <Chip
                        key={group.id}
                        variant="outlined"
                        color="secondary"
                        className="mr-2 mb-2"
                        label={group.name}
                      />
                    ))}
                  {notifyState.individualEmails.length > 0 &&
                    notifyState.individualEmails.map((email) => (
                      <Chip
                        key={email}
                        variant="outlined"
                        color="secondary"
                        className="mr-2 mb-2"
                        label={email}
                      />
                    ))}
                </div>
              </Box>
            </div>

            <NotifyEmailComment
              note={note}
              setNote={setNote}
              SubmitButton={NextButton}
            />
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (activeStep === Steps.ADD_COMMENT) {
      setNote("");
    }
  }, [activeStep]);

  return (
    <div className="px-4 py-3" style={{ maxWidth: "600px" }}>
      <div className="">
        <Typography variant="h5" sx={{ width: "100%" }}>
          <b>{t("share_bulletinCount", { count: bulletinIds.length })}</b>
        </Typography>
      </div>

      {/* Back button */}
      {activeStep === Steps.ADD_COMMENT && (
        <Box mb={2}>
          <Tooltip title={t("back")}>
            <IconButton onClick={handleBack} aria-label="back">
              <ArrowBackIcon fontSize="large" />
            </IconButton>
          </Tooltip>
          <Typography
            variant="body2"
            component="span"
            sx={{ verticalAlign: "middle", ml: 1 }}
          ></Typography>
        </Box>
      )}

      {/* Step content */}
      {renderStepContent()}

      {/* Next button - Only show on first step */}
      {activeStep === Steps.SELECT_RECIPIENTS && (
        <Box mt={3} display="flex" justifyContent="flex-end">
          <NextButton />
        </Box>
      )}
    </div>
  );
};

export default NotifyEmails;
